/**
 * Lifecycle view tabs for the Inbox: Open (active/waiting/human work),
 * Triage (SA-717: open threads waiting on a person's triage decision, with
 * a count), Resolved, Archived. Drives the list endpoint's `view` param.
 *
 * SA-1129: the ARIA tabs keyboard model on plain buttons (a Radix trigger
 * activates on mousedown, which the click tests rely on not doing): one Tab
 * stop on the current tab, the arrows wrap, Home and End go to the ends, and
 * activation is automatic (moving focus selects, as a click does; the list
 * read drops an answer whose view changed meanwhile). Every tab controls the
 * one panel the page marks, the list column.
 */
import { useRef, type KeyboardEvent } from 'react'
import { Badge } from '@/components/ui'
import { cn } from '@/utils'
import type { InboxViewTab } from '@/types/inbox'
import { INBOX_VIEW_PANEL_ID, inboxViewTabId } from './inboxViewIds'
import { tourAnchor } from '@/components/tour/tourAnchor'

const LABELS: Record<InboxViewTab, string> = {
  open: 'Open', triage: 'Triage', resolved: 'Resolved', archived: 'Archived',
}

interface InboxViewTabsProps {
  value: InboxViewTab
  onChange: (tab: InboxViewTab) => void
  /** SA-717: the Triage tab's count. Omitted = no Triage tab (a deployment
   * without email ingestion has nothing to triage). */
  triageCount?: number
}

export function InboxViewTabs({ value, onChange, triageCount }: InboxViewTabsProps) {
  const tabs: InboxViewTab[] = triageCount === undefined
    ? ['open', 'resolved', 'archived']
    : ['open', 'triage', 'resolved', 'archived']
  const buttons = useRef<Partial<Record<InboxViewTab, HTMLButtonElement | null>>>({})

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return
    const last = tabs.length - 1
    const target = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    }[event.key]
    if (target === undefined) return
    event.preventDefault()
    buttons.current[tabs[target]]?.focus()
    // A re-select is not a no-op: the page's view setter clears the status filter.
    if (tabs[target] !== value) onChange(tabs[target])
  }

  // SA-1127: a segmented control (the basis Tabs list's look); the current
  // tab is a raised surface, never a filled primary pill (SA-1123). SA-1129:
  // the card shade plus a hairline, because the basis `bg-background` is the
  // page ground since SA-1059 and all but vanished on the muted track.
  return (
    <div className="inline-flex items-center gap-0.5 rounded-lg bg-muted p-[3px]" role="tablist" aria-label="Inbox views" {...tourAnchor('inbox.toolbar.tabs')}>
      {tabs.map((tab, index) => {
        const current = value === tab
        return (
          <button
            key={tab}
            ref={(el) => { buttons.current[tab] = el }}
            id={inboxViewTabId(tab)}
            type="button"
            role="tab"
            aria-selected={current}
            aria-controls={INBOX_VIEW_PANEL_ID}
            tabIndex={current ? 0 : -1}
            onClick={() => onChange(tab)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={cn(
              'inline-flex h-[26px] items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
              current ? 'border-border bg-card text-foreground shadow-sm' : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {LABELS[tab]}
            {tab === 'triage' && !!triageCount && (
              <Badge variant="warning-soft" className="px-1.5 py-0 text-[10px]">{triageCount}</Badge>
            )}
          </button>
        )
      })}
    </div>
  )
}
